import { getRequestAppModels, RequestAppClientModel } from "client/Api/Apps/AppStore/appStore";
import {
	ContentWithSidebar,
	Dialog,
	NoResults,
	SearchInputForm,
	SidebarHandle,
	Toolbar
} from "client/Components";
import { MakeUserRequestDialog } from "client/Features/Settings/UserRequests/MakeUserRequestDialog";
import { UserRequestKind } from "client/Api/UserRequests/userRequest";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useForceRerender,
	useScrollToTop,
	useUserScopedKey,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { generateTrackingId } from "client/Misc/utils";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { AppCard } from "./AppCard";
import { AppCardList } from "./AppCardList";
import { AppStoreSidebarKey } from "./AppStore";
import { AppStoreLayout } from "./AppStoreLayout";
import {
	allAppsCategory,
	categories,
	CategoryEntry,
	CategoryFilter,
	getCategoryName,
	hiddenCategoryIds
} from "./CategoryFilter";
import { contains, normalizeForSearch } from "client/Data/searching";

export type RequestAppProps = {
	onClose: () => void;
};

export const RequestApp = (props: RequestAppProps): ReactElement => {
	const {
		onClose
	} = props;

	const [isInteractingWithTheBackend, setIsInteractingWithTheBackend] = useState<boolean>(false);
	const [hasError, setHasError] = useState<boolean>(false);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [category, setCategory] = useState<CategoryEntry>(allAppsCategory);
	const [apps, setApps] = useState<readonly RequestAppClientModel[]>([]);
	const [appToRequestAccessFor, setAppToRequestAccessFor] = useState<RequestAppClientModel | undefined>();
	const contentRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const alertManager: AlertManager = useAlerts();
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const sidebarRef: RefObject<SidebarHandle | null> = useRef<SidebarHandle>(null);

	const sidebarExpansionPreferencePersistenceKey: string = useUserScopedKey(AppStoreSidebarKey);
	const [forceReload, appsVersion] = useForceRerender();

	const categoriesForFilter: readonly CategoryEntry[] = useMemo(
		() => categories.filter(x => x.id === null || !hiddenCategoryIds.includes(x.id)),
		[]
	);

	useEffect(
		() => {
			const load = async (signal: AbortSignal): Promise<void> => {
				try {
					setHasError(false);
					setIsInteractingWithTheBackend(true);
					const apps: readonly RequestAppClientModel[] = await getRequestAppModels(signal);
					setApps(apps);
					setIsInteractingWithTheBackend(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsInteractingWithTheBackend(false);
					setHasError(true);
				}
			};

			const cancellation = new AbortController();
			load(cancellation.signal);
			return () => cancellation.abort();
		},
		[alertManager, onClose, appsVersion]
	);

	useScrollToTop(
		contentRef,
		[searchTerm, category]
	);

	const requestHandler = (app: RequestAppClientModel): void => {
		if (app.hasPendingRequest) {
			return;
		}
		setAppToRequestAccessFor(app);
	};

	const normalizedSearchTerm: string = normalizeForSearch(searchTerm);
	const appsToRender: RequestAppClientModel[] = apps.filter(
		(app) => {
			if (category.id !== null) {
				if (app.categoryId !== category.id) {
					return false;
				}
			}

			if (normalizedSearchTerm.length > 0) {
				const matchesName: boolean = !app.name
					|| contains(app.name, normalizedSearchTerm)
					|| app.tags
						.some(t => contains(t.name, normalizedSearchTerm));

				if (!matchesName) {
					return false;
				}
			}

			return true;
		}
	);

	const requestedHandler = (app: RequestAppClientModel): void => {
		setApps(
			(x) => {
				const copy: RequestAppClientModel[] = [...x];
				const index: number = copy.findIndex(x => x.id === app.id);
				if (index !== -1) {
					copy[index] = {
						...copy[index],
						hasPendingRequest: true
					};
				}

				return copy;
			}
		);
		setAppToRequestAccessFor(undefined);
	};

	const hasNoResults = useMemo(
		(): boolean => appsToRender.length === 0,
		[appsToRender]
	);

	const changeCategoryFilterHandler = useCallback(
		(category: CategoryEntry): void => {
			if (isMobile) {
				sidebarRef.current
					?.onClose();
			}

			setCategory(category);
		},
		[isMobile]
	);

	return (
		<>
			<Dialog.Body
				goesEdgeToEdge
			>
				<ContentWithSidebar
					hasBusyIndicator={isInteractingWithTheBackend}
					trackingId="request-app"
				>
					<ContentWithSidebar.Sidebar
						persistentKeyName={sidebarExpansionPreferencePersistenceKey}
						ref={sidebarRef}
					>
						<CategoryFilter
							items={categoriesForFilter}
							value={category}
							onChange={changeCategoryFilterHandler}
						/>
					</ContentWithSidebar.Sidebar>
					<ContentWithSidebar.Content ref={contentRef} hasBackgroundColor>
						<AppStoreLayout fillHeight={hasNoResults}>
							<AppStoreLayout.Header
								search={
									<SearchInputForm
										onChange={setSearchTerm}
										placeholder={localize("_SearchApps")}
										trackingId="search-term"
										variant="white"
									/>
								}
							/>

							<AppStoreLayout.CategoryName name={allAppsCategory.name} />

							{hasError && (
								<NoResults
									trackingId="has-error"
									icon="gear"
									title={localize("_NotAbleToRetrieveContacts")}
									message={localize("_WeWereUnableToRetrieveYourContacts")}
									onRetry={forceReload}
									height="fill"
								/>
							)}
							{(hasNoResults && !hasError)
								? searchTerm
									? (
										<NoResults
											trackingId="no-search-results"
											icon="magnifying-glass"
											title={localize("_NoSearchResults")}
											message={localize("_PleaseTrySearchingWithAnotherTerm")}
											height="fill"
										/>
									)
									: (
										<NoResults
											trackingId="no-apps"
											icon="grid-2"
											title={localize("_NoAppsYet")}
											message={localize("_ThereAreNoAppsAvailableYet")}
											height="fill"
										/>
									)
								: (
									<AppCardList trackingId="card-list">
										{appsToRender.map((x) => (
											<AppCard
												key={`${x.id}`}
												icon={x.imageDisplayInfo}
												color={x.color}
												userLicense={null}
												name={x.name}
												category={getCategoryName(x.categoryId)}
												size="short"
												primaryAction={{
													key: x.hasPendingRequest ? "_Requested" : "_Request",
													kind: x.hasPendingRequest ? "transparent" : "primary",
													isDisabled: x.hasPendingRequest,
													handler: () => requestHandler(x),
													trackingId: "request"
												}}
												trackingId={generateTrackingId("app-request-card", `|${x.id}`)}
												tags={x.tags}
											/>
										))}
									</AppCardList>
								)
							}
						</AppStoreLayout>
					</ContentWithSidebar.Content>
				</ContentWithSidebar>

				{appToRequestAccessFor && (
					<MakeUserRequestDialog
						item={{
							kind: UserRequestKind.AppAccess,
							id: appToRequestAccessFor.id,
							name: appToRequestAccessFor.name
						}}
						onRequested={() => requestedHandler(appToRequestAccessFor)}
						onClose={() => setAppToRequestAccessFor(undefined)}
					/>
				)}
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="secondary"
						onClick={onClose}
						text={localize("_Close")}
						responsiveBehavior="hide"
					/>
				</Toolbar>
			</Dialog.Footer>
		</>
	);
};
RequestApp.displayName = "RequestApp";
